<template>
  <div v-if="template">
    <TransitionRoot appear :show="showModal" as="template">
      <Dialog as="div" @close="closeModal">
        <div class="fixed inset-0 z-30 overflow-y-auto">
          <div class="min-h-screen text-center flex items-center">
            <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0"
              enter-to="opacity-10"
              leave="duration-200 ease-in"
              leave-from="opacity-10"
              leave-to="opacity-0"
            >
              <DialogOverlay class="fixed inset-0 bg-black opacity-30" />
            </TransitionChild>

            <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-50"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-50"
            >
              <div
                class="inline-block w-full my-5 mx-3 md:w-1/2 md:mx-auto max-w-screen-sm h-fit m-auto overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded"
                style="height: fit-content"
              >
                <div class="flex flex-row">
                  <div class="flex-1"></div>
                  <div class="flex-4 pr-2 pt-2 cursor-pointer" @click="closeModal">
                    <XIcon class="text-brand-dark-gray w-7 h-7" ref="close" />
                  </div>
                </div>

                <div class="pb-5 px-5 md:px-8">
                  <h2 class="text-xl text-brand-black">Send documents</h2>

                  <p class="py-2 text-sm">Send documents to signees to view and sign</p>

                  <template v-if="template.linked_form">
                    <p class="py-1">Select Application</p>
                    <AppDropdown :templateSlug="template.linked_form" @select="handleSelectApp" />
                  </template>

                  <AlertToast v-if="showBundleWarning" class="flex flex-col my-5">
                    <div class="flex items-center py-3 px-5">
                      <p class="text-brand-black text-sm">
                        You have selected an application with multiple applicants. This document
                        template has application field values mapped with "display for multiple
                        applicants" not enabled.
                        <span class="font-semibold"
                          >Only the values from the first application received will be
                          displayed.</span
                        >
                      </p>
                    </div>
                  </AlertToast>

                  <div class="flex items-center justify-between w-full py-3 mt-3">
                    <p class="text-brand-black">Signees</p>

                    <div class="w-1/5">
                      <SecondaryButton @click="addSignee" :classes="'px-1'"
                        ><p class="text-sm text-brand-green whitespace-nowrap">
                          + Add Signee
                        </p></SecondaryButton
                      >
                    </div>
                  </div>

                  <div
                    class="rounded-lg border border-brand-text-green py-3 px-3 mb-3 relative"
                    v-for="(signee, idx) in sentTo"
                    :key="idx"
                  >
                    <div class="flex items-center">
                      <div class="flex flex-col py-1 mr-2 w-1/3">
                        <label for="name" class="block"> Name </label>
                        <SingleLineInput
                          id="name"
                          type="text"
                          :value="signee.name"
                          @input="signee.name = $event.target.value"
                          :required="false"
                        ></SingleLineInput>
                      </div>

                      <div class="flex flex-col py-1 mr-2 w-1/3">
                        <label for="email" class="block">
                          Email
                          <span class="text-brand-red">*</span>
                        </label>
                        <SingleLineInput
                          id="email"
                          type="email"
                          :value="signee.email"
                          @input="signee.email = $event.target.value"
                          :required="true"
                        ></SingleLineInput>
                      </div>

                      <div class="w-1/3 flex items-center mt-6">
                        <DropDown
                          :options="sendToOptions"
                          placeholder="Signee type"
                          :initial="
                            signee.type ? sendToOptions.find((o) => o.value === signee.type) : null
                          "
                          @select="signee.type = $event.value"
                        ></DropDown>
                      </div>
                    </div>

                    <div
                      class="rounded-full w-8 h-8 bg-brand-background-light-red flex items-center justify-center cursor-pointer absolute -top-3 -right-3"
                      v-if="idx !== 0"
                      @click="removeSignee(idx)"
                    >
                      <XIcon class="w-5 h-5 text-brand-red" />
                    </div>
                  </div>

                  <div class="flex flex-col pt-3">
                    <label for="subject" class="block"> Subject </label>
                    <SingleLineInput
                      id="subject"
                      type="text"
                      placeholder="Action Required: Please review the documents"
                      :value="subject"
                      @input="subject = $event.target.value"
                    ></SingleLineInput>
                  </div>

                  <div class="flex flex-col pt-5 pb-3">
                    <label for="customMessage" class="block"> Custom message </label>
                    <MultiLineInput
                      id="customMessage"
                      placeholder="Click the button below to sign the documents."
                      :value="customMessage"
                      @input="customMessage = $event.target.value"
                    ></MultiLineInput>
                  </div>

                  <!-- <div class="flex flex-row">
                    <input
                      id="showPhoneInput"
                      type="checkbox"
                      class="rounded border-gray-300 text-brand-green focus:ring-brand-green w-4 h-4"
                      v-model="showPhoneInput"
                    />
                    <label for="showPhoneInput" class="ml-2 block text-sm"
                      >Send link to applicant's phone number</label
                    >
                  </div>

                  <div class="flex flex-row" v-if="showPhoneInput">
                    <SingleLineInput
                      id="phone"
                      type="text"
                      mask="phone"
                      placeholder="Phone Number"
                      :value="phoneNumber"
                      @input="phoneNumber = $event.target.value"
                    ></SingleLineInput>
                  </div> -->

                  <div class="flex items-center justify-end" v-if="errorMessage">
                    <p class="text-brand-red">{{ errorMessage }}</p>
                  </div>

                  <div class="flex flex-row md:justify-end py-3">
                    <div class="flex flex-col-reverse mx-auto md:mx-0 md:flex-row">
                      <p class="text-center py-3 cursor-pointer md:pr-10" @click="closeModal">
                        Cancel
                      </p>

                      <div class="w-48">
                        <PrimaryButton
                          @click="sendShare()"
                          :disabled="isLoading"
                          :is-loading="isLoading"
                          >Send
                        </PrimaryButton>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>
<script>
import _ from "lodash"

import { TransitionRoot, TransitionChild, Dialog, DialogOverlay } from "@headlessui/vue"

import { XIcon } from "@heroicons/vue/outline"

import { DOCUMENT_TEMPLATE_SEND_SIGNING } from "@/store/types"

import { mapGetters } from "vuex"

import AppDropdown from "./AppDropdown.vue"

export default {
  props: {
    showModal: {
      type: Boolean,
      default: false
    },
    template: {
      type: Object
    }
  },
  components: {
    TransitionRoot,
    TransitionChild,
    Dialog,
    DialogOverlay,
    XIcon,
    AppDropdown
  },
  computed: {
    ...mapGetters(["isDocumentTemplateLoading", "getCurrentUser"]),
    allSigneeEmail() {
      return _.every(this.sentTo, "email")
    },
    allSigneeType() {
      return _.every(this.sentTo, "type")
    },
    showBundleWarning() {
      if (!this.selectedBundle || this.selectedBundle.applications.length <= 1 || !this.template) {
        return false
      }

      return this.template.show_bundle_warning
    },
    showMultiSignerDisabledWarning() {
      if (!this.template || this.sentTo.length <= 1) {
        return false
      }

      return Object.keys(this.template.multiple_signees_field_disabled).some(
        (k) =>
          this.template.multiple_signees_field_disabled[k] &&
          this.sentTo.filter((u) => u.type === k).length > 1
      )
    }
  },
  data() {
    return {
      isLoading: false,
      name: "",
      email: "",
      subject: "",
      customMessage: "",
      errorMessage: "",
      sentTo: [
        {
          name: "",
          email: "",
          type: "applicant"
        }
      ],
      sendToOptions: [
        {
          label: "Applicant",
          value: "applicant"
        },
        {
          label: "Landlord",
          value: "landlord"
        },
        {
          label: "Agent",
          value: "agent"
        }
        // {
        //   label: "Other",
        //   value: "other"
        // }
      ],
      selectedBundle: null
    }
  },
  methods: {
    handleSelectApp(bundle) {
      this.selectedBundle = bundle
      this.sentTo = [
        ...bundle.applications.map((a) => {
          return {
            name: a.applicant.name,
            email: a.applicant.email,
            type: "applicant"
          }
        }),
        {
          name: bundle.applications[0].landlord.name,
          email: bundle.applications[0].landlord.email,
          type: "landlord"
        }
      ]
    },
    resetData() {
      Object.assign(this.$data, {
        sentTo: [
          {
            name: "",
            email: "",
            type: "applicant"
          }
        ],
        subject: "",
        customMessage: ""
      })
    },
    addSignee() {
      this.sentTo.push({
        name: "",
        email: "",
        type: "applicant"
      })
    },
    removeSignee(idx) {
      this.sentTo.splice(idx, 1)
    },
    closeModal() {
      this.resetData()
      this.$emit("close")
    },
    async sendShare() {
      const { sentTo, subject, customMessage, selectedBundle } = this

      this.isLoading = true

      if (!this.allSigneeEmail) {
        this.errorMessage = "Email is required for all signees."
        return
      }

      // if (sentTo.find((s) => s.email === this.getCurrentUser.username)) {
      //   this.errorMessage = "You cannot send documents to yourself."
      //   return
      // }

      if (this.showMultiSignerDisabledWarning) {
        this.errorMessage = `This document template has a signature field with "multiple signees" not enabled. Please remove unsupported extra signees to continue.`
        return
      }

      if (!this.allSigneeType) {
        this.errorMessage = "Signee type is required for all signees."
        return
      }

      try {
        await this.$store.dispatch(DOCUMENT_TEMPLATE_SEND_SIGNING, {
          id: this.template.id,
          sentTo,
          subject,
          customMessage,
          selectedBundle
        })
        this.closeModal()
      } catch (err) {
        console.log(err)
      }
      this.isLoading = false
    }
  }
}
</script>
<style scoped></style>
